import {
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { FacadeService } from '../service-facade/facade.service';
import { RbacServiceInterface } from '../../../services/rbac/rbac-service.interface';
import { FilterControlsFactory } from '../../../services/actions/models/filter-controls-factory';
import { DestroySubscribers } from '../../../decorators/destroy-subscribers.decorator';
import { ITableAction, PageSettings, TableInterface } from '../../table-layout/table-layout.model';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { StorageMap } from '@ngx-pwa/local-storage';
import { FacadeServiceInterface, IColumn, IFilter, IRow, ITableData } from '../service-facade/data.response';
import { combineLatest, Subject } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { IFilterModel } from '../../../services/actions/models/filter-model.interface';
import { IFilterValues, IOrderValues, IVisibilityValues } from '../../../services/tables/interfaces/table.service.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { RefreshDirective } from '../../../directives/refresh/refresh.directive';
import { IUnsecuredService } from 'src/app/core/services/unsecured/unsecured-service.interface';
import { TABLE_TRANSLATIONS } from './table-translation.const';
import { IPaginationParams } from './table-params.interfaces';

@Component({
  selector: 'gtd-table-facade-new',
  templateUrl: './table-facade.component.html',
  providers: [
    {
      provide: 'FacadeServiceInterface',
      useClass: FacadeService,
    },
  ],
})
@DestroySubscribers()
export class TableFacadeNewComponent implements OnInit, AfterViewInit {
  private subscribers: any = {};
  private table$ = new Subject<TableInterface>();

  @Input() title: string;
  @Input() key: string;
  @Input() settings: PageSettings;
  @Output() filterChange: EventEmitter<IFilterValues> = new EventEmitter();

  public visibilityValues: IVisibilityValues = {};
  public filtersModel: IFilterModel[] = [];
  public visibleColumns: IColumn[];
  public data: ITableData;
  public tables: TableInterface[];
  public columns: IColumn[] = [];
  public currentSettings: TableInterface;
  public checkboxesGroup?: UntypedFormGroup;
  public orderValues: IOrderValues = {};
  public isScroll = false;
  public filterValues: any;
  public dataNotFoundWithAction = $localize`:@@common.data_not_found_filters_new:Data wasn't found for the current filters, but you can add a new element.`;
  public dataNotFound = $localize`:@@common.data_wasnt_found_filters:Data wasn't found for the current filters.`;
  public pagination: IPaginationParams = { page: 1, limit: 25, total: 100 };

  @ContentChildren(RefreshDirective) refreshItems: QueryList<RefreshDirective>;
  @ViewChild('mainContainer') mainContainer!: ElementRef;

  constructor(
    @Inject('RbacServiceInterface') public rbacService: RbacServiceInterface,
    @Inject('FacadeServiceInterface') public facadeService: FacadeServiceInterface,
    @Inject('IUnsecuredService') public unsecuredService: IUnsecuredService,
    @Inject(StorageMap) private localStorage: StorageMap,
    private cd: ChangeDetectorRef,
    private filterControlsFactory: FilterControlsFactory,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.route.queryParams.pipe(take(1)).subscribe((params) => (this.pagination.page = params?.page || 1));
  }

  ngOnInit(): void {
    if (this.settings[0]?.type === 'widget') {
      this.pagination.limit = 10;
    }
    this.load();
    this.init();
  }

  ngAfterViewInit() {
    this.subscribers.unsecured = this.unsecuredService.getUnsecuredStatus().subscribe(() => {
      this.updateContainerHeight();
    });
  }

  @HostListener('window:resize')
  onResize() {
    this.updateContainerHeight();
  }

  private init() {
    combineLatest([this.localStorage.get(`table_${this.key}`), this.route.queryParams])
      .pipe(take(1))
      .subscribe(([savedKey, routeParams]) => {
        this.facadeService.onChangePage(this.pagination.page);
        if (Array.isArray(this.settings)) {
          this.tables = this.settings;
          const tableKey = routeParams?.tableKey || savedKey;
          const currentTable = this.tables.find((table) => table.key === tableKey);
          this.onTableChange(currentTable || this.tables[0]);
        } else {
          this.onTableChange(this.settings);
        }
      });
  }

  load(): void {
    const table$ = this.table$.asObservable();
    this.subscribers.facadeServiceInit = table$
      .pipe(
        switchMap((table) =>
          this.facadeService.init(table.key, this.pagination.limit, this.currentSettings.defaultFilters, this.currentSettings.scopeValues)
        )
      )
      .subscribe((data) => {
        this.data = data;
        this.pagination = {
          total: data.total,
          page: data.page,
          limit: data.limit,
        };
        this.columns = this.currentSettings.columnsIdsToHide
          ? data.columns.filter((column) => !this.currentSettings.columnsIdsToHide.includes(column.id))
          : data.columns;

        this.visibleColumns = this.mapColumns(this.columns);

        if (this.checkboxesGroup) {
          this.setControl(data.rows);
        }

        this.translateFilterLabel(data.filters);
        this.translateColumnLabel(data.columns);

        this.filterChange.emit(data.filterValues);
        const filtersValue = data.filterValues;
        this.filterValues = data.filterValues;
        this.filtersModel = this.data.filters.map((field) => {
          const { group } = field;
          const modelValidation = this.filtersModel.find((item) => item.model.getComponent().settings.key === field.id);
          const control = modelValidation?.model.getComponent().settings.control ?? new UntypedFormControl();
          control.setValue(filtersValue[field.id] || null);

          return {
            group,
            model: this.filterControlsFactory.getField(field.type, field.id, field.label, control),
            isQuickFilter: field.quickSearch,
          };
        });
        this.updateContainerHeight();
        setTimeout(() => {
          try {
            const body = document.querySelector('.p500-ui-table-wrapper');
            this.isScroll = body.clientHeight < body.scrollHeight;
          } catch {}
        }, 10);
      });
  }

  initVisibility(): void {
    this.localStorage
      .get('column_visibility_' + this.currentSettings.key + this.currentSettings.type)
      .subscribe((data) => (this.visibilityValues = (data as IVisibilityValues) || {}));
  }

  mapColumns(columns: IColumn[]): IColumn[] {
    return columns
      .filter((column) => (this.visibilityValues[column.id] !== undefined ? this.visibilityValues[column.id] : column.visible_by_default))
      .map((column) =>
        column.type === 'type_protected'
          ? {
              ...column,
              isUnsecured: this.unsecuredService.isUnsecuredValue,
            }
          : column
      );
  }

  resetData(): void {
    this.data = null;
    this.visibleColumns = null;
    this.cd.detectChanges();
  }

  handleSort(orderValues: IOrderValues): void {
    this.resetData();
    this.orderValues = orderValues;
    this.facadeService.onChangeSort(orderValues);
  }

  update(): void {
    this.resetData();
    this.facadeService.onChangePage(1);
    this.refreshItems.forEach((item) => {
      if ('getData' in item.gtdRefresh) {
        item.gtdRefresh.getData();
      }
    });
  }

  onPageChange(page: number): void {
    this.resetData();
    this.router.navigate([], { queryParams: { page } });
    this.facadeService.onChangePage(page);
  }

  onFilterChange(filters: IFilterValues): void {
    this.router.navigate([], { queryParams: { page: 1 } });
    this.pagination.page = 1;
    this.facadeService.onChangePage(this.pagination.page);
    this.filtersModel = this.filtersModel.map((field) => {
      field.model.formControl.setValue(filters[field.model.key] || null);
      return field;
    });
    this.resetData();
    this.resetCheckboxGroup();
    this.facadeService.onChangeFilters(filters);
    this.filterChange.emit(filters);
  }

  onLimitChange(limit: number): void {
    this.router.navigate([], { queryParams: { page: 1 } });
    this.localStorage.set('display_limit_' + this.currentSettings.key, limit).subscribe();
    this.resetData();
    this.resetCheckboxGroup();
    this.facadeService.onChangeLimit(limit);
    this.facadeService.onChangePage(1);
  }

  onVisibilityChange(visibilityValues: IVisibilityValues): void {
    this.localStorage.set('column_visibility_' + this.currentSettings.key + this.currentSettings.type, visibilityValues).subscribe();
    this.visibilityValues = { ...visibilityValues };
    this.visibleColumns = this.mapColumns(this.columns);
  }

  onTableChange(table: TableInterface): void {
    this.data = null;
    this.localStorage.set(`table_${this.key}`, table.key).subscribe();
    this.currentSettings = table;
    this.checkboxesGroup = table.checkboxesConfig?.formGroup;
    if (this.checkboxesGroup) {
      table.checkboxesConfig.formGroup.reset({});
    }
    this.initVisibility();
    this.table$.next(table);
  }

  private resetCheckboxGroup(): void {
    if (this.checkboxesGroup) {
      this.checkboxesGroup.reset({});
    }
  }

  private setControl(rows: IRow[]): void {
    for (const { id } of rows) {
      if (!this.checkboxesGroup.get([id])) {
        this.checkboxesGroup.addControl(id, new UntypedFormControl());
      }
    }
  }

  private translateFilterLabel(filters: IFilter[]): void {
    filters.forEach((filter) => {
      filter.label = TABLE_TRANSLATIONS[filter.label] || filter.label;
      filter.group = TABLE_TRANSLATIONS[filter.group] || filter.group;
    });
  }

  private translateColumnLabel(columns: IColumn[]): void {
    return columns.forEach((col) => {
      col.label = TABLE_TRANSLATIONS[col.label] || col.label;
    });
  }

  getActionProps(action: ITableAction): Partial<any> {
    return action.passTableData?.status
      ? {
          filterValues: this.filterValues || {},
          visibilityValues: this.visibilityValues,
          group: action.passTableData?.value,
          ...action.props,
        }
      : action.props;
  }

  private updateContainerHeight(): void {
    if (!this.currentSettings || this.currentSettings?.type === 'widget') {
      return;
    }
    const container = this.mainContainer?.nativeElement;
    if (container) {
      const offsetTop = container.getBoundingClientRect().top;
      const availableHeight = window.innerHeight - offsetTop - 33;
      container.style.height = `${availableHeight}px`;
    }
  }
}
